// Native & external imports
import { firstValueFrom } from "rxjs";
import { Injectable, DOCUMENT, inject } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ApiCallsService, EnvSetupObject } from "icamppro-shared-components";
import { HttpResponseObject } from "@iclassproinc/icamppro-shared-components";
import { environment } from "src/environments/environment";
// Routes
import { AppRoutes } from "src/routing/routes";
// Services
import { AppRouterService } from 'src/routing/routes-service';
import { AppLocalization } from '@icamppro-customer-portal/shared/cmp-base';
// Objects
import {
  CmpAppConfigObject,
  CmpAppConfigObjectHelper as helper,
  CmpMaintenanceModeObject,
} from "src/objects/cmp-app-config.object";
import { MaintenanceModeService } from "./maintenance-mode.service";
import { CMP_ENV_SETUP } from "../app/cmp-env-setup.injection-token";

/**
 * Class used to load application configurations such as the account's configuration.
 */
@Injectable({ providedIn: "root" })
export class AppLoadService {
  private apiService = inject(ApiCallsService);
  private routerService = inject(AppRouterService);
  private _appLocalization = inject(AppLocalization);
  private titleService = inject(Title);
  private maintenanceModeService = inject(MaintenanceModeService);
  private document = inject<Document>(DOCUMENT);
  cmpEnv = inject<EnvSetupObject>(CMP_ENV_SETUP);

  private static cachedEnvSetupObject: EnvSetupObject | undefined;

  /**
   * These are the default settings for the account config.
   * **DO NOT USE THESE DIRECTLY!** Instead use the `CmpAppSettings` service class.
   * **Purposely not exported.**
   */
  private static readonly _appConfigDefaults: Readonly<CmpAppConfigObject> = {
    appName: "iCampPro",
    appTitle: "iCampPro Registration Portal",
    timeZone: "America/Chicago",
    locale: "en-US",
    currencyName: "USD",
    companyName: "the camp",
    customerServicePhoneNumber: "",
    customerServiceEmail: "",
  };

  private static readonly _appMaintenanceModeSettings: Readonly<CmpMaintenanceModeObject> =
    {
      maintenanceMode: false,
      scheduled: false,
      message: "",
      title: "",
      scheduledTime: "",
    };

  // private properties
  private tagManager = "";
  private gtmAuth = "";
  private gtmEnvironment = "";
  private _appSettings: CmpAppConfigObject | undefined = undefined;
  public maintenanceModeSettings: CmpMaintenanceModeObject =
    new CmpMaintenanceModeObject();

  public static initEnvSetupObject(): EnvSetupObject {
    // this method with no args is needed for module providers
    return AppLoadService.getEnvSetupObject(false);
  }
  public static getEnvSetupObject(refresh: boolean = false): EnvSetupObject {
    if (refresh || typeof AppLoadService.cachedEnvSetupObject === "undefined") {
      const envSetupValue = new EnvSetupObject(
        environment.apiUrl,
        environment.s3Env,
        environment.assetPrefix,
        AppRoutes.unAuthorizedUrls,
        "camper",
      );
      envSetupValue.s3AssetEnv = environment.s3AssetEnv;
      AppLoadService.cachedEnvSetupObject = envSetupValue;
    }
    return AppLoadService.cachedEnvSetupObject;
  }

  public get appSettings(): CmpAppConfigObject {
    return this._appSettings || AppLoadService._appConfigDefaults;
  }

  public get getAppSettings() {
    if (this._appSettings.companyName === "the camp") {
      return false;
    }
    return true;
  }

  public get appMaintenanceSettings(): CmpMaintenanceModeObject {
    return (
      this.maintenanceModeSettings || AppLoadService._appMaintenanceModeSettings
    );
  }

  constructor() {
    this._appSettings = AppLoadService._appConfigDefaults;
    this.maintenanceModeSettings = AppLoadService._appMaintenanceModeSettings;
  }
  /**
   * @method initializeGTMSettings
   * @description  This is a async function it helps  to get the googleTagManagerId
   */
  private async initializeGTMSettings(): Promise<boolean> {
    const response: HttpResponseObject<any> = await firstValueFrom(
      this.apiService.get(
        `${this.routerService.backendApiUrl.integrationsSettings}`,
      ),
    );
    // this.createGTMCode(response.data.googleTagManagerId)
    if (response && !response.hasErrors && response.data) {
      this.tagManager = response.data.googleTagManagerId;
      this.gtmAuth = response.data.authValue;
      this.gtmEnvironment = response.data.environmentId;
      if (this.gtmEnvironment && !this.gtmEnvironment.match("env-")) {
        this.gtmEnvironment = `env-${this.gtmEnvironment}`;
      }
      if (!this.gtmEnvironment) {
        this.createGTMCode();
      } else {
        this.createGTMCodeForEnv();
      }
    }
    return await true;
  }

  /**
   * @method createGTMCode
   * @description  This is function helps  to set the googleTagManager code in the dom
   */
  private createGTMCodeForEnv() {
    const script = this.document.createElement("script");
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=${this.gtmAuth}&gtm_preview=${this.gtmEnvironment}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${this.tagManager}');`;
    this.document.getElementsByTagName("head")[0].appendChild(script);
    const noscript = this.document.getElementsByTagName("noscript")[0];
    if (noscript) {
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.tagManager}&gtm_auth=${this.gtmAuth}&gtm_preview=${this.gtmEnvironment}&gtm_cookies_win=x"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    }
    return true;
  }

  /**
   * @method createGTMCode
   * @description  This is function helps  to set the googleTagManager code in the dom
   */
  private createGTMCode() {
    const script = this.document.createElement("script");
    script.innerHTML = `(function (w, d, s, l, i) {
            w[l] = w[l] || []; w[l].push({
                'gtm.start':
                    new Date().getTime(), event: 'gtm.js'
            }); var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true; j.src =
                    'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer','${this.tagManager}');`;
    this.document.getElementsByTagName("head")[0].appendChild(script);
    const noscript = this.document.getElementsByTagName("noscript")[0];
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.tagManager}" height="0" width="0"
          style="display:none;visibility:hidden"></iframe>`;
    return true;
  }

  /**
   * Used to initialize the account's configuration.
   *
   * @remarks
   * This is async because it is not possible to return from inside a `Observable<T>.subscribe()`
   * callback or from within a `Promise<T>.then()` callback. But, by making it async we can handle
   * the return value as expected.
   *
   * @param forceReload - If this is `true` it will hit the api to get a fresh set of data.
   * @returns The current account configuration.
   */
  public async initAppSettingsAsync(
    forceReload = false,
  ): Promise<CmpAppConfigObject> {
    this.initializeGTMSettings();

    if (document.location.href.includes("pinecove")) {
      this.titleService.setTitle("Pine Cove - Customer Portal");
    } else if (document.location.href.includes("campskylark")) {
      this.titleService.setTitle("iCamppro - Customer Portal");
    } else {
      this.titleService.setTitle("iCamppro - Customer Portal");
    }

    if (!forceReload) {
      if (typeof this._appSettings !== "undefined") {
        return this._appSettings;
      } else {
        // the this.defaultAppSettings object is empty, so lets get it from the api
        return await this.initAppSettingsAsync(true);
      }
    } else {
      await this.maintenanceModeService.checkForMaintenance();
      const response: HttpResponseObject<any> = await firstValueFrom(
        this.apiService.get(
          `${this.routerService.backendApiUrl.getAppSettings}`,
          { appSettings: true },
        ),
      );
      return this.updateSettingsFromHttpResponse(response);
    }
  }

  /**
   * This is the only place to update the account settings!
   *
   * @param response - the http response from getting the account settings.
   */
  private updateSettingsFromHttpResponse(
    response: HttpResponseObject<any>,
  ): CmpAppConfigObject {
    if (
      response &&
      !response.hasErrors &&
      helper.isNullableCmpAppConfigObject(response.data)
    ) {
      this._appSettings = helper.merge(
        this._appSettings || AppLoadService._appConfigDefaults,
        response.data,
      );
      this._appLocalization.updateAccount(this._appSettings);
    }
    if (typeof this._appSettings === "undefined") {
      this._appSettings = AppLoadService._appConfigDefaults;
      this._appLocalization.updateAccount(this._appSettings);
    }
    return this._appSettings;
  }
}
